import React, { useEffect, useMemo } from "react";
import { ControlLabel, FormGroup, HelpBlock, PanelGroup } from 'react-bootstrap';
import { useTranslation } from "react-i18next";
import { Assignment, ExpirationGap, IsExpirationLessThan } from "../../../api/Inventory/Entities/InventoryEntities";
import { MoveTypes } from "../../../api/Inventory/Entities/InventoryMovementEntities";
import Loader from "../../../components/Common/Loader";
import useInteractionGate from "../Hooks/useInteractionGate";
import useInventoryCart from "../Hooks/useInventoryCart";
import { buildCartLotConsumption, MISSING_COMMENT_STYLE } from "../Shared/inventoryFormRules";
import { buildPharmacyPatientDestination } from "../Shared/patientDestination";
import PharmacyItemField from "./PharmacyItemField";

export default function PharmacyFormField({
    disabled,
    name,
    onChange,
    medicalRecordNumber,
    visitId,
    value
}) {
    const { t } = useTranslation();
    const assignment = Assignment.GeneralAssigment();
    const dispenseValue = useMemo(() => firstDispense(value), [value]);
    const destination = useMemo(
        () => buildPharmacyPatientDestination(medicalRecordNumber, visitId),
        [medicalRecordNumber, visitId]
    );
    const isPatientUnidentified = destination == null;
    const isReadOnly = disabled || isPatientUnidentified;
    const [move, isMoveLoading, actions] = useInventoryCart(dispenseValue?.id ?? 0,
        MoveTypes.PharmacyDispense.Name,
        assignment.assignmentType, assignment.assignId,
        destination, dispenseValue)

    const { hasInteracted, markInteracted } = useInteractionGate();

    useEffect(() => {
        if (!hasInteracted()) return;
        onChange(name, [{ ...move }])
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [move, name, onChange])

    function handleNewItem(item) {
        actions.AddItem(item.item, item.release, item.quantity * 1, item.assignment, item.availableQuantity)
        markInteracted();
    }

    function onRemoveItem(item, _) {
        actions.RemoveItem(item)
        markInteracted();
    }

    function hasAnyExpiringLot() {
        if (move.items == null || move.items.length == 0) return false;

        return move.items.some(item => IsExpirationLessThan(item.release, ExpirationGap.Critical));
    }

    function isCommentEditable() {
        return !isReadOnly && hasAnyExpiringLot();
    }

    function isCommentMissing() {
        return isCommentEditable() && (move.comment == null || move.comment.trim() === "");
    }

    function onHandleComment(e) {
        const value = e.target.value;
        actions.SetComment(value)
        markInteracted();
    }

    return (
        <Loader isLoading={isMoveLoading}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem', backgroundColor: 'white', padding: '10px', }}>
                <div style={{ textAlign: 'center' }}>
                    <h5>
                        <strong>{t('inventory.dispense.pharmacyDispenseTitle')}</strong>
                    </h5>
                    <hr />
                </div>
                {isPatientUnidentified && (
                    <p className="text-muted" style={{ textAlign: 'center' }}>
                        {t('inventory.dispense.patientVisitDataMissing')}
                    </p>
                )}
                <PanelGroup
                    accordion
                    id="multi-items-pharmacy"
                    defaultActiveKey="multi-item--1">
                    {move != null && move.items.map((item, index) => (<PharmacyItemField
                        movementItem={item}
                        index={`${index}`}
                        disabled={true}
                        assignmentType={assignment.assignmentType}
                        assignId={assignment.assignId}
                        handleSubmit={handleNewItem}
                        handleRemove={onRemoveItem}
                        hasActions={true}
                        actionsDisbled={isReadOnly}
                        isDispensed={move?.id > 0}
                        lotConsumption={buildCartLotConsumption(move.items, item)}
                    />))}
                    {!isReadOnly && <PharmacyItemField
                        movementItem={null}
                        index={"-1"}
                        disabled={isReadOnly}
                        assignmentType={assignment.assignmentType}
                        assignId={assignment.assignId}
                        handleSubmit={handleNewItem}
                        bsStyle="info"
                        lotConsumption={buildCartLotConsumption(move.items)}
                    />}
                </PanelGroup>
                <div>
                    <FormGroup style={{ width: "100%" }} validationState={isCommentMissing() ? "error" : null}>
                        <ControlLabel>{t("commons.comment")}</ControlLabel>
                        <textarea className="form-control"
                            rows={3}
                            value={move.comment}
                            name="movecomment"
                            style={isCommentMissing() ? MISSING_COMMENT_STYLE : undefined}
                            disabled={!isCommentEditable()}
                            onChange={onHandleComment}
                        />
                        {isCommentMissing() && <HelpBlock>{t('inventory.dispense.commentRequiredForExpiringLot')}</HelpBlock>}
                    </FormGroup>
                </div>
            </div>
        </Loader>)
}

function firstDispense(value) {
    if (value == null) return null;
    if (Array.isArray(value)) {
        return value.find(m => MoveTypes.IsEquivalent(m?.moveType, MoveTypes.PharmacyDispense)) ?? null;
    }
    return value;
}